/**
 * Performance Settings Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the usePerformanceSettings hook.
 */
import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Save, BookOpen } from 'lucide-react';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { useAuth } from '../../contexts/AuthContext';

// Import modular components
import {
  OTIFSettingsTab,
  PPMSettingsTab,
  RatingFormulaTab,
  GradeThresholdsTab,
  GradeColourPaletteSection,
  PerformanceGuideTab,
  SiteKPISettingsTab,
  DeliveryMetricsRecalcCard,
  usePerformanceSettings,
  useOTIFGradeThresholds,
  usePPMGradeThresholds,
  SupplierOTIFData,
} from './performance';

export default function PerformanceSettings() {
  const [activeTab, setActiveTab] = useState('otif');
  const qualityLabel = useQualityMeasureLabel();
  const { user } = useAuth();
  const isAdmin = Boolean(
    user?.is_superuser || user?.company_type === 'helix' || user?.is_company_admin,
  );

  const {
    // State
    loading,
    saving,
    settings,
    hasChanges,
    formData,
    // Threshold state
    editingThreshold,
    thresholdEditData,
    savingThreshold,
    showAddThreshold,
    newThresholdData,
    addingThreshold,
    // Supplier state
    showSupplierOverrides,
    supplierSearch,
    editingSupplier,
    supplierEditData,
    savingSupplier,
    // State setters
    setShowAddThreshold,
    setShowSupplierOverrides,
    setSupplierSearch,
    setSupplierEditData,
    // Handlers
    handleInputChange,
    handleWeightChange,
    handleSave,
    handleResetThresholds,
    handleResetOTIFMappings,
    handleResetPPMMappings,
    handleEditThreshold,
    handleCancelEditThreshold,
    handleSaveThreshold,
    handleThresholdFieldChange,
    handleAddThreshold,
    handleDeleteThreshold,
    handleNewThresholdFieldChange,
    handleEditSupplier,
    handleCancelEditSupplier,
    handleSaveSupplier,
    handleToggleSupplierCustom,
    loadSettings,
  } = usePerformanceSettings();

  // CR-6267: OTIF colour bands are their own resource — separate state and
  // endpoint from the supplier grade thresholds above.
  const otifGrades = useOTIFGradeThresholds(settings?.otif_grade_thresholds ?? [], loadSettings);

  // CR-6652: PPM colour bands are their own resource — separate state and
  // endpoint from OTIF grades and supplier rating thresholds.
  const ppmGrades = usePPMGradeThresholds(
    settings?.ppm_grade_thresholds ?? [],
    loadSettings,
    qualityLabel,
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading performance settings...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Supplier Performance Settings</h2>
          <p className="text-sm text-muted-foreground">
            Configure OTIF and {qualityLabel} calculation thresholds, rating formula weights, and score mappings.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="otif">OTIF Settings</TabsTrigger>
          <TabsTrigger value="ppm">{qualityLabel} Settings</TabsTrigger>
          <TabsTrigger value="rating">Rating Formula</TabsTrigger>
          <TabsTrigger value="grades">Grade Thresholds</TabsTrigger>
          <TabsTrigger value="kpi">Site KPI Settings</TabsTrigger>
          <TabsTrigger value="guide" className="flex items-center gap-1">
            <BookOpen className="h-4 w-4" />
            Guide
          </TabsTrigger>
        </TabsList>

        {/* OTIF Settings Tab */}
        <TabsContent value="otif" className="space-y-4">
          {isAdmin && <DeliveryMetricsRecalcCard />}
          <OTIFSettingsTab
            settings={settings}
            formData={formData}
            otifGrades={otifGrades}
            showSupplierOverrides={showSupplierOverrides}
            supplierSearch={supplierSearch}
            editingSupplier={editingSupplier}
            supplierEditData={supplierEditData}
            savingSupplier={savingSupplier}
            onInputChange={handleInputChange}
            onResetOTIFMappings={handleResetOTIFMappings}
            onSetShowSupplierOverrides={setShowSupplierOverrides}
            onSetSupplierSearch={setSupplierSearch}
            onSetSupplierEditData={setSupplierEditData}
            onEditSupplier={(supplier) => handleEditSupplier(supplier as SupplierOTIFData)}
            onCancelEditSupplier={handleCancelEditSupplier}
            onSaveSupplier={handleSaveSupplier}
            onToggleSupplierCustom={(supplier) => handleToggleSupplierCustom(supplier as SupplierOTIFData)}
          />
        </TabsContent>

        {/* PPM Settings Tab */}
        <TabsContent value="ppm">
          <PPMSettingsTab
            settings={settings}
            formData={formData}
            ppmGrades={ppmGrades}
            onInputChange={handleInputChange}
            onResetPPMMappings={handleResetPPMMappings}
          />
        </TabsContent>

        {/* Rating Formula Tab */}
        <TabsContent value="rating">
          <RatingFormulaTab
            formData={formData}
            onWeightChange={handleWeightChange}
          />
        </TabsContent>

        {/* Grade Thresholds Tab */}
        <TabsContent value="grades" className="space-y-4">
          {/* CR-7079: one palette, cascaded to every scorecard. Sits above the
              band editor because a colour change here overrides the per-band
              colour below for any standard grade. */}
          <GradeColourPaletteSection
            settings={settings}
            onSaved={loadSettings}
            readOnly={!isAdmin}
          />
          <GradeThresholdsTab
            settings={settings}
            editingThreshold={editingThreshold}
            thresholdEditData={thresholdEditData}
            savingThreshold={savingThreshold}
            showAddThreshold={showAddThreshold}
            newThresholdData={newThresholdData}
            addingThreshold={addingThreshold}
            onSetShowAddThreshold={setShowAddThreshold}
            onResetThresholds={handleResetThresholds}
            onEditThreshold={handleEditThreshold}
            onCancelEditThreshold={handleCancelEditThreshold}
            onSaveThreshold={handleSaveThreshold}
            onThresholdFieldChange={handleThresholdFieldChange}
            onAddThreshold={handleAddThreshold}
            onDeleteThreshold={handleDeleteThreshold}
            onNewThresholdFieldChange={handleNewThresholdFieldChange}
          />
        </TabsContent>

        {/* Site KPI Settings Tab (CR-5883) */}
        <TabsContent value="kpi">
          <SiteKPISettingsTab />
        </TabsContent>

        {/* Guide Tab */}
        <TabsContent value="guide">
          <PerformanceGuideTab
            settings={settings}
            formData={formData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
